import {
  ESellerTypes,
  IAdminCreateSellerRequest,
  IAdminUpdateSellerRequest,
  ISellerFormData
} from 'models/seller'
import {
  IEmployeeUpdateSellerRequest,
  TEmployeeAccessSection
} from 'models/sellerEmployees'
import { normalizePhone } from 'shared/utils/normalizePhone'

const toOptionalTrimmed = (value?: string) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export const getLegalSubmitData = (
  data: ISellerFormData
): IAdminCreateSellerRequest => {
  return {
    seller_info: {
      type: data.type,
      name: data.firstName.trim(),
      surname: data.lastName.trim(),
      patronymic: data?.middleName?.trim(),
      opt_otch: data.noMiddleName,
      email: data.email.trim(),
      tel: normalizePhone(data.phone),
      password: data.password.trim()
    },
    banking_details: {
      name: data?.bankName?.trim() as string,
      INN: data.legalINN as string,
      BIK: data.bankBIK as string,
      PC: data.bankPC as string,
      KC: data.bankKC as string,
      actual_address: data.actualAddress as string,
      edo_operator: data.edoOperator?.trim(),
      edo_id: data.edoId?.trim()
    },
    shop_details: {
      shop_name: data.shopName.trim(),
      desc: data.desc.trim()
    },
    logo: data.logo[0].originFileObj,
    banner: data.banner[0].originFileObj,
    default_tax: data.defaultTax
  }
}

export const getEditSubmitData = (
  data: ISellerFormData,
  sellerId: string,
  toDeletePhotos: string[]
): IAdminUpdateSellerRequest => {
  const legalAddress =
    data.type === ESellerTypes.LEGAL
      ? toOptionalTrimmed(data?.legalAddress)
      : undefined
  const legalActualAddress =
    data.type === ESellerTypes.LEGAL
      ? toOptionalTrimmed(data?.actualAddress)
      : undefined
  const registrationAddress =
    data.type === ESellerTypes.PHYSICAL
      ? toOptionalTrimmed(data?.registrationAddress)
      : undefined
  const physicalActualAddress =
    data.type === ESellerTypes.PHYSICAL
      ? toOptionalTrimmed(data?.actualAddress)
      : undefined

  return {
    seller_info: {
      type: data.type,
      name: data.firstName.trim(),
      surname: data.lastName.trim(),
      patronymic: data?.middleName?.trim(),
      opt_otch: data.noMiddleName,
      email: data.email.trim(),
      tel: normalizePhone(data.phone),
      birthday: data.birthDate ? new Date(data.birthDate).toString() : undefined
    },
    banking_details: {
      name: data?.bankName?.trim(),
      BIK: data?.bankBIK,
      KPP: data.type === ESellerTypes.LEGAL ? data.legalKPP : data.physicalKPP,
      PC: data?.bankPC,
      KC: data?.bankKC,
      INN: data.type === ESellerTypes.LEGAL ? data.legalINN : data.physicalINN,
      ...(legalAddress ? { legal_address: legalAddress } : {}),
      ...(legalActualAddress ? { actual_address: legalActualAddress } : {})
    },
    shop_details: {
      shop_name: data.shopName.trim(),
      desc: data.desc.trim()
    },
    company_info: {
      name: data?.companyName?.trim(),
      okpo: data?.OKPO,
      ogrn: data?.OGRN,
      type: data?.organizationType
    },
    logo: data.logo[0]?.originFileObj ?? data.logo[0].uid,
    banner: data.banner[0]?.originFileObj ?? data.banner[0].uid,
    passport_photo:
      data?.passportPhoto?.[0]?.originFileObj ?? data?.passportPhoto?.[0]?.uid,
    seller_id: sellerId,
    toDeletePhotos,
    passport_details: data?.passportData,
    card_number: data?.cardNumber,
    edo_operator: toOptionalTrimmed(data?.edoOperator),
    edo_id: toOptionalTrimmed(data?.edoId),
    ...(registrationAddress
      ? { registration_address: registrationAddress }
      : {}),
    ...(physicalActualAddress ? { actual_address: physicalActualAddress } : {}),
    default_tax: data.defaultTax
  }
}

export const getEmployeeSubmitData = (
  data: ISellerFormData,
  sellerId: string,
  openSections: TEmployeeAccessSection[]
): IEmployeeUpdateSellerRequest => ({
  seller_id: sellerId,
  ...(openSections.includes('SHOP')
    ? {
      shop_details: {
        shop_name: data.shopName.trim(),
        desc: data.desc.trim()
      },
      default_tax: data.defaultTax,
      logo: data.logo[0]?.originFileObj ?? data.logo[0]?.uid,
      banner: data.banner[0]?.originFileObj ?? data.banner[0]?.uid
    }
    : {}),
  // пустая строка стирает реквизит: без поля бэкенд оставил бы прежнее значение
  ...(openSections.includes('BANKING')
    ? {
      banking_details: {
        name: data.bankName?.trim() ?? '',
        BIK: data.bankBIK ?? '',
        PC: data.bankPC ?? '',
        KC: data.bankKC ?? ''
      },
      ...(data.type === ESellerTypes.PHYSICAL
        ? { card_number: data.cardNumber ?? '' }
        : {})
    }
    : {})
})
